import { MAX_FILES_PER_UPLOAD, MAX_UPLOAD_MB } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, FileDropzone, Icon, IngestionRow, type FileRejection, type IngestionStage } from "@sagot/ui";
import { formatBytes } from "../shared/format";
import { StorageFullActions } from "../shared/storage-full";
import { consoleStage } from "../shared/ingestion";
import type { SourceView } from "../shared/types";
import { uploadStage, type UploadItem } from "./use-uploads";

const K = "deployKnowledge.knowledge.add";

interface FilesPanelProps {
  formId: string;
  pending: File[];
  onPendingChange: (files: File[]) => void;
  uploads: UploadItem[];
  sources: readonly SourceView[];
  error: string | undefined;
  onSubmit: () => void;
  onRetry?: ((key: string) => void) | undefined;
}

export function useStageLabels(): Partial<Record<IngestionStage, string>> {
  const { t } = useTranslation();
  const s = "deployKnowledge.knowledge.stages";
  return {
    uploading: t(`${s}.uploading`),
    parsing: t(`${s}.parsing`),
    chunking: t(`${s}.chunking`),
    contextualizing: t(`${s}.contextualizing`),
    embedding: t(`${s}.embedding`),
    ready: t(`${s}.ready`),
    failed: t(`${s}.failed`),
  };
}

interface UploadRowsProps {
  uploads: readonly UploadItem[];
  sources: readonly SourceView[];
  /** Offered on failures worth retrying (storage / server faults). */
  onRetry?: ((key: string) => void) | undefined;
  /** Accessible name of the list; defaults to "Uploads". */
  label?: string | undefined;
}

/** Upload rows: byte progress, then the live pipeline stage of the created source (SSE-fed cache). */
export function UploadRows({ uploads, sources, onRetry, label }: UploadRowsProps) {
  const { t } = useTranslation();
  const labels = useStageLabels();
  return (
    <ul className="space-y-2" aria-label={label ?? t(`${K}.uploadsTitle`)} aria-live="polite">
      {uploads.map((item) => {
        const source = sources.find((s) => s.id === item.sourceId);
        const live = source ? consoleStage({ status: source.status, stage: source.stage }) : undefined;
        const stage = uploadStage(item, live);
        const error = item.error ?? (stage === "failed" ? (source?.status_reason ?? undefined) : undefined);
        const progress = stage === "uploading" ? item.progress : (source?.progress ?? 0);
        return (
          <IngestionRow
            key={item.key}
            item={{ id: item.key, name: item.name, stage, progress, ...(error ? { error } : {}) }}
            labels={item.outcome === "unchanged" ? { ...labels, ready: t(`${K}.unchanged`) } : labels}
            action={
              item.outcome === "failed" && item.storageFull === true ? (
                <StorageFullActions />
              ) : item.outcome === "failed" && item.retryable === true && onRetry ? (
                <Button type="button" variant="secondary" size="sm" aria-label={t(`${K}.retryFile`, { name: item.name })} onClick={() => { onRetry(item.key); }}>
                  <Icon name="refresh-cw" />
                  {t(`${K}.retry`)}
                </Button>
              ) : undefined
            }
          />
        );
      })}
    </ul>
  );
}

export function FilesPanel({ formId, pending, onPendingChange, uploads, sources, error, onSubmit, onRetry }: FilesPanelProps) {
  const { t } = useTranslation();
  const rejected = ({ file, reason }: FileRejection) =>
    ({
      type: t(`${K}.rejectType`, { name: file.name }),
      size: t(`${K}.rejectSize`, { name: file.name, mb: MAX_UPLOAD_MB }),
      empty: t(`${K}.rejectEmpty`, { name: file.name }),
      count: t(`${K}.rejectCount`, { name: file.name, max: MAX_FILES_PER_UPLOAD }),
    })[reason];
  return (
    <form id={formId} noValidate className="space-y-4" onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
      <FileDropzone
        maxFiles={MAX_FILES_PER_UPLOAD - pending.length}
        onFiles={(files) => { onPendingChange([...pending, ...files]); }}
        labels={{ title: t(`${K}.dropTitle`), hint: t(`${K}.dropHint`, { mb: MAX_UPLOAD_MB }), dragActive: t(`${K}.dropActive`), rejected }}
      />
      {pending.length > 0 ? (
        <ul className="space-y-2" aria-label={t(`${K}.selectedFiles`)}>
          {pending.map((file, index) => (
            <li key={`${file.name}-${String(index)}`} className="flex items-center gap-3 rounded-md border border-line bg-surface px-3 py-2">
              <Icon name="file-text" className="size-4 text-primary-ink" />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{file.name}</span>
              <span className="text-xs text-fg-subtle">{formatBytes(file.size)}</span>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={t(`${K}.remove`, { name: file.name })}
                onClick={() => { onPendingChange(pending.filter((_, i) => i !== index)); }}
              >
                <Icon name="x" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <p className="text-xs font-semibold text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {uploads.length > 0 ? <UploadRows uploads={uploads} sources={sources} onRetry={onRetry} /> : null}
      <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <Icon name="scan-text" className="size-3.5" />
        {t(`${K}.ocrNote`)}
      </p>
    </form>
  );
}
